import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { api } from '@/api';
import {
  useMedicalHistoryQuery,
  usePatientStudiesQuery,
  useReportQuery,
  useStudyMedicalHistoryQuery,
} from '@/api/queries';
import { Loading } from '@/shared/ui';
import { generateAppRoute } from '@/shared/util';
import { useOrganValidationStore, useReportOrganStore } from '@/store';

import { Header } from './header';
import { NavMenu, NavMenuProvider } from './nav-menu';
import { ReportingState } from './reporting-state.component';
import { useRouteValidation } from './use-route-validation.hook';
import { getSelectedLink } from './util';

export function ReportingLayout() {
  const [selectedLink, setSelectedLink] = useState<string>('');

  const { organ } = useParams();
  const location = useLocation();
  const isValidRoute = useRouteValidation();

  const { setReportOrgans } = useReportOrganStore();
  const { getStatus, setStatus } = useOrganValidationStore();

  const { isLoading: isReportLoading, report } = useReportQuery();
  const { isLoading: isMedicalHistoryLoading } = useMedicalHistoryQuery();
  const { isLoading: isStudyMedicalHistoryLoading } = useStudyMedicalHistoryQuery();
  const { isLoading: isPatientStudiesLoading } = usePatientStudiesQuery();

  const { data: reportOrgans, isLoading: isReportOrgansLoading } =
    api.fct.reportOrgan.query.useGetReportOrgans(
      {
        payload: {
          reportUuid: report?.uuid || '',
        },
      },
      {
        enabled: Boolean(report?.uuid),
      },
    );

  const isLoading =
    isReportLoading ||
    isMedicalHistoryLoading ||
    isStudyMedicalHistoryLoading ||
    isPatientStudiesLoading ||
    isReportOrgansLoading;

  const handleLinkSelect = (link: string) => {
    setSelectedLink(link);
  };

  useEffect(() => {
    setSelectedLink(getSelectedLink(location, organ));
  }, [organ, location]);

  useEffect(() => {
    if (reportOrgans && report) {
      setReportOrgans(report.uuid, reportOrgans);

      reportOrgans.forEach(({ organ: organName, status }) => {
        const organStatus = getStatus(organName);

        setStatus(organName, {
          done: status === 'published',
          error: organStatus?.error ?? false,
          warning: organStatus?.warning ?? false,
        });
      });
    }
  }, [reportOrgans, report]);

  if (isValidRoute) {
    return (
      <div className="flex h-screen flex-col">
        <Header />
        <div className="flex h-[calc(100%-5rem)] p-4 pb-0 pr-0">
          <NavMenuProvider onLinkSelect={handleLinkSelect} selectedLink={selectedLink}>
            <NavMenu />
          </NavMenuProvider>
          <div className="custom-scrollbar relative flex-1 overflow-y-auto">
            {isLoading ? <Loading /> : <ReportingState />}
          </div>
        </div>
      </div>
    );
  }

  return <Navigate to={generateAppRoute('NOT_FOUND')} />;
}
